import React, { useState } from 'react';
import { X, Sparkles, Bug, Wrench, Shield, BarChart3, Eye } from 'lucide-react';

const CHANGELOG = [
  {
    version: '9.0',
    date: 'Oct 2026',
    items: [
      { type: 'feature', text: 'Firearm gallery cards now show manufacturer & model banner' },
      { type: 'feature', text: 'Vehicle, Jewelry, Electronics and Art gallery cards now show a name banner' },
      { type: 'feature', text: 'Real Estate map view — every property pinned on one map' },
      { type: 'feature', text: 'Real Estate gallery cards show the property address — tap it to open in Maps' },
      { type: 'improve', text: 'Gallery banners color-coded per asset type' },
      { type: 'feature', text: 'Clickable version number opens this What\'s New changelog' },
      { type: 'fix', text: 'Removed stale offline cache that caused "newer version available" prompts' },
      { type: 'fix', text: 'Document/PDF viewer loads Cloudinary files reliably' },
      { type: 'fix', text: 'Portfolio loads faster — no live price lookups for non-market assets' },
      { type: 'improve', text: 'Maintenance log entries can be added on vehicles again' },
      { type: 'fix', text: 'Performance chart cleaned — only shows market platforms' },
      { type: 'fix', text: 'Startup migration auto-cleans production database on deploy' },
    ]
  },
  {
    version: '8.8',
    date: 'Oct 2026',
    items: [
      { type: 'feature', text: 'Performance Trend chart with zoom/pan and $ / % toggle' },
      { type: 'feature', text: 'Daily portfolio snapshots run automatically at 11:55 PM CT' },
      { type: 'feature', text: 'Historical data backfilled with interpolation for smooth charts' },
      { type: 'feature', text: 'Version tracking added to footer' },
    ]
  },
  {
    version: '8.7',
    date: 'Sep 2026',
    items: [
      { type: 'feature', text: 'Platform-aware Import/Export — bulk replace stocks by platform' },
      { type: 'fix', text: 'Electronics detail modal now saves (was hitting wrong API endpoint)' },
      { type: 'fix', text: 'Vehicle count reflects hidden sold items' },
      { type: 'improve', text: 'Gallery defaults to compact size' },
      { type: 'improve', text: 'Sold items hidden by default on all asset tabs' },
      { type: 'fix', text: 'Firearms Make/Model sort now works correctly' },
    ]
  },
  {
    version: '8.5',
    date: 'Aug 2026',
    items: [
      { type: 'feature', text: 'Two-Factor Authentication via email code (per-user toggle)' },
      { type: 'security', text: 'All panels collapse on login for privacy' },
      { type: 'security', text: 'All values hidden on login until manually revealed' },
      { type: 'fix', text: 'Art detail modal saves all fields (artist, medium, year)' },
      { type: 'fix', text: 'Non-market asset refresh no longer blocks the entire backend' },
      { type: 'feature', text: 'Deployment date tracked in analytics' },
    ]
  },
  {
    version: '8.0',
    date: 'Jul 2026',
    items: [
      { type: 'feature', text: 'Private Investment/Loans detail modal with full field set' },
      { type: 'feature', text: 'Sold/Disposed toggle for all non-market assets' },
      { type: 'improve', text: 'Strikethrough text and gray background on sold items' },
      { type: 'feature', text: 'Hide Sold toggle per asset type tab' },
      { type: 'fix', text: 'Disposed items excluded from all portfolio totals' },
      { type: 'feature', text: 'SMTP password visibility toggle in admin' },
      { type: 'feature', text: 'Backup email with test button' },
      { type: 'improve', text: 'Collapsible panels on Backup page' },
      { type: 'feature', text: 'All collections included in backups' },
    ]
  },
];

const typeConfig = {
  feature: { icon: Sparkles, color: 'text-emerald-500', bg: 'bg-emerald-50', label: 'New' },
  fix: { icon: Bug, color: 'text-red-400', bg: 'bg-red-50', label: 'Fix' },
  improve: { icon: Wrench, color: 'text-blue-400', bg: 'bg-blue-50', label: 'Improved' },
  security: { icon: Shield, color: 'text-violet-400', bg: 'bg-violet-50', label: 'Security' },
};

const VersionChangelog = ({ currentVersion, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="changelog-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-slate-800 to-slate-900 text-white px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold">What's New</h2>
            <p className="text-slate-400 text-xs">Current version: v{currentVersion}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Changelog */}
        <div className="overflow-y-auto max-h-[calc(80vh-80px)] p-5 space-y-6">
          {CHANGELOG.map((release, ri) => (
            <div key={release.version}>
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${
                  ri === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                }`}>
                  v{release.version}
                </span>
                <span className="text-xs text-slate-400">{release.date}</span>
                {ri === 0 && <span className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide">Latest</span>}
              </div>
              <div className="space-y-2 ml-1">
                {release.items.map((item, i) => {
                  const config = typeConfig[item.type] || typeConfig.improve;
                  const Icon = config.icon;
                  return (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className={`mt-0.5 p-1 rounded ${config.bg}`}>
                        <Icon className={`w-3 h-3 ${config.color}`} />
                      </span>
                      <span className="text-sm text-slate-700">{item.text}</span>
                    </div>
                  );
                })}
              </div>
              {ri < CHANGELOG.length - 1 && <div className="border-b border-slate-100 mt-4" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VersionChangelog;
export { CHANGELOG };
